'use client'

import { parseWelcomeCoupon, type WelcomeCoupon } from '@/lib/marketing/welcomeCouponWire'
import * as Sentry from '@sentry/nextjs'
import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Loader2, FlaskConical, AlertTriangle } from 'lucide-react'
import type { CartItem, CustomerInfo, DropoffAddress, HeldQuote, ShipQuote } from '../types'
import type { TableContext } from './tableContext'
import { AcceptedCards } from '../AcceptedCards'
import { formatDropoffFull } from '@/lib/delivery/formatDropoff'
import { type PricingConfig, effectiveCashDiscountPct } from '@/lib/utils/pricing'
import { donationCharge } from "@/lib/donations/donationCharge"
import { orderPaymentReference } from '@/lib/orders/orderNumber'
import { loadCheckoutScript, waitForCheckoutGlobal, waitForElement } from '@/lib/north/checkoutScript'
import { micrositeNorthProduct, parseSessionCouponRejection, type MicrositeLocale, type SessionCouponRejection } from '@/lib/microsite/couponUx'
import { parseVisitRewardRejection, parseZeroWriteVisitRewardRejection, type VisitRewardRejection } from '@/lib/microsite/visitRewardUx'
import { isSessionAvailabilityRejection, retailRejectionMessage } from '@/lib/microsite/cartAvailability'
import { session503Outcome } from './session503'
import { buildMicrositeOrderItems } from '@/lib/microsite/buildMicrositeOrderItems'
import type { CouponSnapshot } from '@/lib/coupons/validate'
import { shipErrorMessageKey } from './shipErrorUx'
import type { SubstitutionPref, SubstitutionWarning } from '@/lib/online-orders/substitutionPrefs'
import { SubstitutionPrefSelector } from './SubstitutionPrefSelector'
import { attributionBodyField } from '@/lib/tracking/attribution'
import { cartValue, track, trackItemsFromCart } from '@/lib/tracking/storefrontEvents'
import { parseConversion, type Conversion } from '@/lib/tracking/conversionValue'

type Props = {
  locationId: string
  /** Slug del local, para enlazar su política de devoluciones en el consentimiento. */
  slug: string
  cart: CartItem[]
  customer: CustomerInfo
  subtotal: number
  taxAmount: number
  /** Precios con impuesto incluido: la fila se rotula "incluido" (no se suma). */
  taxIncluded?: boolean
  /** PHASE F: per-transaction processing fee charged at checkout. 0 when feature off. */
  processingFee?: number
  /**
   * Envío en dólares SOLO para mostrarlo en el resumen. El monto que se cobra sale
   * de `deliveryQuote.feeAmountCents` (y del servidor, que lo re-resuelve por
   * quoteId): esto es display, nunca la fuente de verdad.
   */
  deliveryFee?: number
  total: number
  accentColor: string
  pricingCfg: PricingConfig
  /** Idioma de la tienda: separador decimal del peso en el nombre que va a North. */
  locale?: MicrositeLocale
  /** ISO UTC del pickup agendado, o null para pedido ASAP. */
  scheduledFor: string | null
  donationAmount?: number
  donationCampaignId?: string | null
  /** Modo mesa (pedido por QR): presente ⇒ el POST manda qr_token en vez de location_id. */
  tableContext?: TableContext
  /** Elección del cliente en modo QR: en la mesa (default) o para llevar. */
  qrOrderType?: "dine_in" | "takeout"
  // ─── Delivery (Fase 4b) ────────────────────────────────────────────────────
  /**
   * Cotización CONGELADA del envío. `feeAmountCents` es LA verdad del envío: se
   * manda tal cual a /api/online-orders. Recalcularlo o re-derivarlo produce un
   * 422 DESPUÉS del cobro ("cobrado, sin orden"). null ⇒ pickup.
   */
  deliveryQuote?: HeldQuote | null
  /** Dirección de entrega confirmada. null ⇒ pickup. */
  dropoff?: DropoffAddress | null
  /**
   * La cotización venció (o el local apagó delivery) a mitad del checkout:
   * /session devolvió 409 `delivery_unavailable`. El padre borra el quote y manda
   * al cliente de vuelta al paso de dirección. Reintentar el MISMO POST no puede
   * funcionar — de ahí que este camino reemplace al botón genérico de reintento.
   */
  onDeliveryQuoteExpired?: () => void
  /**
   * Propina al repartidor en CENTAVOS (spec 2026-09-12). Solo en delivery: viaja
   * a /session como `deliveryTipCents` (se cobra como su propia línea) y al POST
   * de la orden como `delivery_tip_cents`, en centavos enteros, sin recalcular.
   */
  driverTipCents?: number
  // ─── Envío (Fase 1b T11) ────────────────────────────────────────────────────
  /**
   * Cotización CONGELADA del envío. `amountCents` es LA verdad — se manda solo
   * el `quoteId` a /session (`shipTo` NO viaja: el servidor usa el `ship_to`
   * VERIFICADO por EasyPost que ya congeló `/api/shipping/quote`). null ⇒ no
   * es un pedido de envío.
   */
  shipQuote?: ShipQuote | null
  /**
   * El quote de envío dejó de ser válido — 409/422 `shipping_unavailable` de
   * /session (consumido, vencido, huella distinta, estado bloqueado…). El
   * padre borra el quote y manda al cliente de vuelta al paso de dirección:
   * reintentar el MISMO POST no puede funcionar.
   */
  onShipQuoteExpired?: () => void
  // ─── Cupón (spec §7.2) ────────────────────────────────────────────────────
  /**
   * Código del cupón aplicado, o null. El padre solo lo manda cuando el cupón
   * está descontando de verdad. Acá NO viaja ningún monto: el descuento lo
   * recomputa `/session` desde precios de la BD contra la fila viva del cupón.
   */
  couponCode?: string | null
  /** Descuento en dólares, SOLO para el resumen en pantalla. */
  couponDiscount?: number
  /**
   * Premio de visitas a canjear (PR 5b), o null. Solo viaja el ID: /session exige la
   * sesión del comensal con el teléfono verificado igual al del pedido, acuña el
   * cupón del premio y recomputa el descuento. Excluyente con `couponCode`.
   */
  visitRewardId?: string | null
  /** Nombre del premio, SOLO para el resumen en pantalla. */
  visitRewardLabel?: string | null
  /** `/session` rechazó el premio (PRE-cobro: nadie pagó). El padre lo suelta y vuelve al carrito. */
  onVisitRewardRejected?: (rejection: VisitRewardRejection) => void
  // ─── Sustituciones (spec sustituciones §4.2, Task 5 los usa en el POST) ───
  /** REQUERIDO a propósito: ningún caller futuro debe olvidar decidir el flag. */
  substitutionsEnabled: boolean
  /** Preferencia de la ORDEN entera ("similar" por defecto). */
  substitutionPref: SubstitutionPref
  onSubstitutionPrefChange: (pref: SubstitutionPref) => void
  /**
   * El cliente dejó un teléfono usable y no rechazó SMS — gobierna si
   * "preguntarme primero" es una opción real del selector. Calculada por
   * OrderSheet con la MISMA regla que usa para CartStep/ConfirmStep
   * (`!!normalizePhone(customer.phone) && !customer.smsDecline`): se pide
   * EXPLÍCITA en vez de recalculada acá para que las tres pantallas nunca
   * puedan divergir sobre qué cuenta como "se puede avisar por SMS".
   */
  contactAllowed: boolean
  /**
   * `/session` rechazó el cupón (422, PRE-cobro: nadie pagó nada). El padre
   * suelta el cupón, corrige el total y devuelve al carrito con el aviso. Sin
   * este camino el cliente vería "no pudimos iniciar el pago" por un cupón que
   * se agotó — o peor, se le cobraría el total sin descuento sin avisarle.
   */
  onCouponRejected?: (rejection: SessionCouponRejection) => void
  /**
   * `/session` rechazó el pedido porque algo del carrito se agotó, se ocultó o
   * salió de su horario (409/422, PRE-cobro: nadie pagó nada). El padre vuelve a
   * mirar el menú fresco, quita esas líneas y devuelve al carrito con el aviso.
   * Sin este camino el cliente veía el texto crudo y "Reintentar" mandaba el mismo
   * carrito, en bucle. Súper online (Tarea 2b.3, R2): para un rechazo retail
   * (`out_of_stock`, peso/variante inválidos…) llega el `message` del servidor, que
   * nombra el producto; para todo lo demás, undefined (el aviso de siempre).
   */
  onItemsUnavailable?: (message?: string) => void
  /** Task 11: true cuando el carrito nació de "Volver a pedir" (prop `fromPrefill`
   *  del padre, sin estado nuevo). SOLO telemetría — ver la nota en submitOrder. */
  fromPrefill?: boolean
  /**
   * «Dominio propio»: el Embedded Checkout de North solo monta en useredy.com.
   * Presente ⇒ tras crear la sesión NO se monta North aquí: se le entrega el
   * token al padre, que guarda lo necesario y lleva al cliente a pagar a
   * useredy.com (lib/microsite/hostedPayment.ts). El $0 y los modos demo o
   * simulación no pasan por aquí: no montan North.
   */
  onHostedHandoff?: (sessionToken: string) => void
  onSuccess: (
    orderId: string,
    orderNumber: string,
    receiptToken: string | null,
    /** Link firmado para guardar la tarjeta en el Wallet, o null si esta plataforma no lo soporta. */
    walletSaveUrl: string | null,
    /** Link firmado para guardar el pedido en la cuenta Redy, o null. */
    accountClaimUrl: string | null,
    /** Link al seguimiento en vivo (/p/<código>), o null si no se pudo acuñar el código. */
    trackingUrl: string | null,
    /** Cupón de bienvenida por aceptar SMS de promociones, o null. */
    welcomeCoupon: WelcomeCoupon | null,
    /** Task 5: preferencia y avisos de sustitución que devolvió el servidor
     *  (`/api/online-orders` o el sondeo de `intent-status`). undefined cuando
     *  ese camino no los trae todavía. */
    extras?: {
      substitutionPref?: SubstitutionPref
      substitutionWarnings?: SubstitutionWarning[]
      /** Valor de la compra para el pixel, calculado por el servidor (orders.subtotal). */
      conversion?: Conversion
    },
  ) => void
  onBack: () => void
}

function normalizePhone(phone: string): string {
  const trimmed = phone.trim()
  // Preserve leading "+" if the user typed an explicit international format
  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length === 0) return ''
  if (hasPlus) return `+${digits}`
  // US 10-digit local: prepend country code
  if (digits.length === 10) return `+1${digits}`
  // 11 digits starting with 1: US with country code, no plus
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  // Any other length: prepend "+" so it still matches E.164 server-side
  if (digits.length >= 10) return `+${digits}`
  // Less than 10 digits — invalid; return digits unchanged so server
  // validation produces a deterministic rejection.
  return digits
}

export function PaymentStep({
  locationId,
  slug,
  cart,
  customer,
  subtotal,
  taxAmount,
  taxIncluded = false,
  processingFee = 0,
  deliveryFee = 0,
  total,
  accentColor,
  pricingCfg,
  locale = 'es',
  scheduledFor,
  donationAmount,
  donationCampaignId,
  tableContext,
  qrOrderType,
  deliveryQuote = null,
  dropoff = null,
  onDeliveryQuoteExpired,
  driverTipCents = 0,
  shipQuote = null,
  onShipQuoteExpired,
  couponCode = null,
  couponDiscount = 0,
  visitRewardId = null,
  visitRewardLabel = null,
  onVisitRewardRejected,
  substitutionsEnabled,
  substitutionPref,
  onSubstitutionPrefChange,
  contactAllowed,
  onCouponRejected,
  onItemsUnavailable,
  fromPrefill = false,
  onHostedHandoff,
  onSuccess,
  onBack,
}: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const tCommon = useTranslations('common')
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formMounted, setFormMounted] = useState(false)
  const [simulationMode, setSimulationMode] = useState(false)
  const [demoMode, setDemoMode] = useState(false)
  const [sessionReady, setSessionReady] = useState(false)
  const [handingOff, setHandingOff] = useState(false)
  const [retryCounter, setRetryCounter] = useState(0)
  // 409 `delivery_unavailable`: reintentar el mismo POST siempre volvería a fallar,
  // así que este flag apaga el botón genérico de reintento y lo reemplaza por la
  // vuelta al paso de dirección (única salida real).
  const [deliveryExpired, setDeliveryExpired] = useState(false)
  // Mismo criterio para `shipping_unavailable` (409/422/502 de /session): el
  // `reason` exacto se traduce con `t(shipErrorMessageKey(reason))` justo
  // antes de `setError` — nunca texto hardcodeado (ronda de fix 1, IMPORTANT-3).
  const [shipExpired, setShipExpired] = useState(false)

  // Pedido a domicilio: hace falta la cotización Y la dirección. El quoteId es lo
  // único que viaja del envío al servidor — el monto lo re-resuelve él desde
  // delivery_quotes, así que el cliente no puede inflar ni abaratar el envío.
  const isDelivery = !!deliveryQuote && !!dropoff && !tableContext
  const deliveryQuoteId = isDelivery ? deliveryQuote.quoteId : null
  const tipCents = isDelivery && driverTipCents > 0 ? driverTipCents : 0
  // Envío: solo el quoteId viaja a /session (CRITICAL-2 de esa ruta: delivery y
  // shipping son mutuamente excluyentes, así que basta con `shipQuote`). El
  // `ship_to` NO se reenvía — el servidor usa el que ya verificó EasyPost.
  const isShip = !!shipQuote && !tableContext
  const shippingQuoteId = isShip ? shipQuote.quoteId : null

  const pct = effectiveCashDiscountPct(pricingCfg)
  const donAmount = donationAmount ?? 0
  const donCharge = donationCharge(donAmount, "card", pct)
  const donationChargeCents = Math.round(donCharge * 100)

  const mountedRef = useRef(true)
  const sessionTokenRef = useRef<string | null>(null)
  // Ancla del carrito que /session validó ANTES de cobrar. Se reenvía al crear la
  // orden para que el servidor pueda persistir ESE carrito si el body llega con
  // modificadores fuera de las reglas del grupo (ver
  // lib/online-orders/intentSnapshotItems.ts). Puede quedar en null: el intent es
  // best-effort en /session y su ausencia solo deja la orden sin ancla.
  const intentIdRef = useRef<string | null>(null)
  // Evita doble onSuccess si el path normal y el sondeo de rescate coinciden.
  const successFiredRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Medición de anuncios (no-op sin pixel): llegó al paso de pago. Una vez por
  // montaje, con el carrito de ese momento.
  useEffect(() => {
    const items = trackItemsFromCart(cart, pricingCfg)
    track('add_payment_info', { value: cartValue(items), currency: 'USD', items })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fallback de confirmación: si North captura el cobro pero nunca dispara
  // onPaymentComplete (iframe sin callback — caso real orden #089 en mesa QR),
  // el sweep server-side crea la orden desde el intent. Este sondeo detecta el
  // intent 'fulfilled' y avanza a la confirmación igual, en vez de dejar al
  // cliente viendo el recibo de North para siempre.
  useEffect(() => {
    if (!sessionReady || demoMode || simulationMode) return
    const token = sessionTokenRef.current
    if (!token) return
    const iv = setInterval(async () => {
      if (successFiredRef.current || !mountedRef.current) return
      try {
        const res = await fetch(`/api/online-orders/intent-status?token=${encodeURIComponent(token)}`)
        if (!res.ok) return
        const data = (await res.json()) as {
          status?: string
          order?: { id: string; order_number: string | null }
          // El servidor decide plataforma y firma; aquí solo se transporta.
          walletSaveUrl?: string | null
          accountClaimUrl?: string | null
          trackingUrl?: string | null
          substitutionPref?: SubstitutionPref
          conversion?: unknown
        }
        if (data.status === 'fulfilled' && data.order && mountedRef.current && !successFiredRef.current) {
          successFiredRef.current = true
          // Sondeo de RESCATE. Gana la carrera casi siempre: /complete crea la orden
          // en el SERVIDOR un segundo después del cobro, así que cuando el submitOrder
          // del navegador llega, este poll ya la vio. Por eso el link tiene que venir
          // de aquí — pasar null pintaba la confirmación sin el botón de guardar.
          onSuccess(data.order.id, orderPaymentReference(data.order.id, data.order.order_number), sessionTokenRef.current, data.walletSaveUrl ?? null, data.accountClaimUrl ?? null, data.trackingUrl ?? null, parseWelcomeCoupon(data), { substitutionPref: data.substitutionPref, conversion: parseConversion(data.conversion) })
        }
      } catch {
        /* transitorio — se reintenta en el próximo tick */
      }
    }, 5000)
    return () => clearInterval(iv)
    // onSuccess viene estable del padre; las deps reales son sesión y modos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady, demoMode, simulationMode])

  useEffect(() => {
    let cancelled = false
    let unsubscribe: (() => void) | undefined

    // Reset state when retrying so the form re-mounts cleanly.
    setError(null)
    setSimulationMode(false)
    setDemoMode(false)
    setFormMounted(false)
    setSessionReady(false)
    setDeliveryExpired(false)
    setShipExpired(false)

    async function init() {
      // Base de tarjeta redondeada (display en el formulario de North); los
      // deltas de modificador quedan exactos. El cobro real es server-autoritativo
      // (priceOnlineOrder re-precia desde la BD), así que esto solo alinea el form.
      const products = cart.map((item) => micrositeNorthProduct(item, pricingCfg, locale))

      let token: string
      try {
        const res = await fetch('/api/online-orders/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locationId,
            // PHASE F: bundle processingFee into the subtotal sent to North so
            // the customer's authorized charge equals subtotal + fee + tax,
            // matching what the server stores. The fee surfaces as its own
            // line on Redy's UI and receipt — the bundling only affects North's
            // checkout iframe, which doesn't itemize it.
            subtotalCents: Math.round((subtotal + processingFee) * 100),
            taxCents: Math.round(taxAmount * 100),
            donationChargeCents,
            // Monto a-la-causa y campaña: van al snapshot de reconciliación para
            // que una orden rescatada por el sweep conserve la donación completa
            // (sin esto el sweep moría en 422 amount_mismatch — caso Zulianisimo).
            donationAmountCents: Math.round(donAmount * 100),
            ...(donationCampaignId ? { donationCampaignId } : {}),
            // Propina al repartidor: SOLO con quote de delivery (el servidor
            // rechaza 400 si viene sin él). Centavos enteros, sin derivar.
            ...(deliveryQuoteId && tipCents > 0 ? { deliveryTipCents: tipCents } : {}),
            products,
            // El server valida disponibilidad y recalcula el monto AUTORITATIVO
            // desde estos ítems ANTES de cobrar (lib/online-orders/pricing). Esto
            // elimina el "cobrado sin orden": un ítem 86'd o un monto que no cuadra
            // se rechaza aquí, sin cobro. Misma forma que el POST de la orden.
            items: buildMicrositeOrderItems(cart),
            // Cupón: viaja el CÓDIGO, jamás un monto. /session lo valida contra
            // la fila viva y recomputa el descuento desde precios de la BD; lo
            // que esta pantalla mostró fue vista previa. Se omite la clave sin
            // cupón para que el body quede byte-idéntico al de siempre.
            ...(couponCode ? { couponCode } : {}),
            // Premio de visitas: solo el ID (omitido sin premio ⇒ body byte-idéntico).
            ...(visitRewardId ? { visitRewardId } : {}),
            // Sustituciones (spec sustituciones §4.2): preferencia de la ORDEN
            // completa elegida en este paso. Se omite con el flag apagado para
            // que el body quede byte-idéntico al de siempre — /session ya trata
            // su ausencia como 'similar'.
            ...(substitutionsEnabled ? { substitution_pref: substitutionPref } : {}),
            // Task 11 (fix ronda 2): mismo booleano que ya llega a submitOrder
            // (ver más abajo) — /session lo copia a `payload.from_prefill` en
            // el intent para que el pedido que /complete crea vía
            // recoverOrderFromIntent (camino normal con tarjeta) también
            // dispare reorder_complete. Se omite en vez de mandar `false`,
            // mismo criterio que el POST directo.
            ...(fromPrefill ? { from_prefill: true } : {}),
            // Medición de anuncios: de dónde llegó el cliente (cookie de la
            // tienda). /session lo copia al payload del intent, que es lo que
            // crea la orden en el camino con tarjeta. Sin cookie, se omite. En la
            // mesa (QR) no: el cliente ya está en el local y la venta no es del
            // anuncio que haya visto antes.
            ...(tableContext ? {} : attributionBodyField()),
            // Snapshot del cliente para el intent de reconciliación: si el cobro
            // se captura pero esta web no llega a crear la orden (Apple Pay), el
            // sweep server-side la crea con estos datos. Teléfono ya normalizado
            // a E.164 para que pase la validación de /api/online-orders.
            customer: {
              name: customer.name.trim(),
              // `|| undefined` OMITE la clave cuando no hay teléfono (mismo patrón
              // que email/notes abajo). Un pedido de mesa dine_in deriva phone=""
              // (buildTableCustomer); mandar "" como clave PRESENTE reventaba el
              // schema (.min(1)/.optional() tolera ausencia, no un "" presente),
              // devolviendo 400 antes de North → el cliente sentado no podía pagar.
              phone: normalizePhone(customer.phone) || undefined,
              email: customer.email.trim() || undefined,
              // Nota libre para la cocina → columna `orders.notes`. Antes iba como
              // `pickup_time` (timestamptz) y reventaba el insert ("cobrado, sin orden").
              notes: customer.pickupNote.trim() || undefined,
              // El teléfono ES el consentimiento (la divulgación va al lado del
              // campo en CustomerStep). Se manda derivado para que el snapshot
              // del intent no contradiga al servidor, pero /api/online-orders lo
              // vuelve a derivar por su cuenta: la autoridad es el servidor, que
              // además consulta la revocación real (STOP) en `customers`.
              sms_opt_in: !!normalizePhone(customer.phone),
              // "No quiero SMS" del checkout (30923). Viaja también en el
              // snapshot para que un replay del sweep no resucite el SMS de
              // alguien que lo declinó al pagar. Solo puede BAJAR el consent.
              sms_decline: customer.smsDecline === true,
              // Consentimiento de MARKETING (promociones del restaurante) —
              // separado de sms_decline arriba. Va también en este snapshot
              // para que el sweep de reconciliación no lo pierda si el
              // pedido se rescata desde este intent.
              //
              // ⚠️ Mandarlos desde acá no alcanza: los DOS tienen que estar
              // declarados en `customerSchema` de /api/online-orders/session
              // —que no lleva `.passthrough()`— y copiados al payload del
              // intent, que se arma campo por campo. Si alguien los saca de
              // allá, zod los descarta y estas dos líneas dejan de tener
              // efecto sin que nada falle.
              marketing_opt_in: customer.marketingOptIn === true,
            },
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            // Billing ZIP for AVS — only the cardholder's own zip is sent (never
            // the restaurant address); omitted when the customer left it blank.
            ...(customer.billingZip.trim() ? { billingZip: customer.billingZip.trim() } : {}),
            ...(scheduledFor ? { scheduledFor } : {}),
            // Modo mesa (QR): al snapshot del intent, para que si esta web no
            // llega a crear la orden (North sin callback) el sweep la cree CON
            // mesa y canal — no como pickup online (caso orden #089).
            ...(tableContext
              ? { qr_token: tableContext.qrToken, qr_order_type: qrOrderType ?? "dine_in" }
              : {}),
            // Delivery (Fase 4b). ⚠️ El envío NO se suma a `subtotalCents` — eso es
            // lo que se hace con el fee de PROCESAMIENTO (arriba) y aplicarlo acá
            // COBRARÍA EL ENVÍO DOS VECES: el servidor ya suma su propio
            // deliveryFeeCents (resuelto por quoteId) al total. Peor: la
            // reconciliación del `adjustment` seguiría cuadrando, así que North no
            // lo atraparía. Solo viaja el quoteId + el snapshot de la dirección.
            ...(deliveryQuoteId
              ? {
                  deliveryQuoteId,
                  dropoffAddress: {
                    street: dropoff!.street,
                    city: dropoff!.city,
                    state: dropoff!.state,
                    zip: dropoff!.zip,
                    ...(dropoff!.lat != null && dropoff!.lng != null
                      ? { lat: dropoff!.lat, lng: dropoff!.lng }
                      : {}),
                  },
                  // Teléfono del cliente ya normalizado a E.164 (el courier lo usa
                  // para avisar en la entrega). Se omite si no hay.
                  ...(normalizePhone(customer.phone)
                    ? { dropoffPhone: normalizePhone(customer.phone) }
                    : {}),
                }
              : {}),
            // Envío (Fase 1b). Solo el quoteId: `/session` re-resuelve el fee
            // leyendo `shipping_quotes` (congelado al cotizar) y usa el `ship_to`
            // que EasyPost ya verificó — nunca lo que el cliente escribió. Mutuamente
            // excluyente con `deliveryQuoteId` (CRITICAL-2 de esa ruta: mandar los
            // dos juntos es 422 `delivery_and_shipping_exclusive`).
            ...(shippingQuoteId ? { shippingQuoteId } : {}),
          }),
        })

        if (cancelled) return

        // Premio de visitas rechazado (PRE-cobro: /session rechaza antes de crear la
        // sesión de pago). Va ANTES del manejo del 503 (si no, `unavailable` caería
        // en modo simulación) y del cupón (el 422 del premio comparte forma con él).
        // Solo con `visitRewardId` en el body: sin él, este bloque no corre.
        if (visitRewardId && !res.ok && onVisitRewardRejected) {
          const rejected = parseVisitRewardRejection(res.status, await res.clone().json().catch(() => null))
          if (rejected) {
            onVisitRewardRejected(rejected)
            return
          }
        }

        if (res.status === 503) {
          // Distinción IMPORTANTE: un 503 SIN cuerpo (o de cualquier otro tipo)
          // es el camino de siempre — North sin credenciales configuradas, modo
          // simulación. Pero hay fallas REALES que no pueden simular (un "pago
          // simulado" sobre un pedido de verdad): `shipping_intent_unavailable` con
          // un envío en vuelo (no se pudo anclar el intent del envío) y, desde la
          // Tarea 2b.3 (R2), `pricing_unavailable` — el cotizador no pudo leer algo
          // ANTES de cobrar (mixto, alcohol del cupón, variantes del súper). Nadie
          // pagó: es un error con "Reintentar". La regla vive en session503.ts.
          const data = await res.json().catch(() => ({}))
          const outcome = session503Outcome(data, !!shippingQuoteId)
          if (outcome.kind === 'ship_failed') {
            throw new Error(t(shipErrorMessageKey('quote_failed')))
          }
          if (outcome.kind === 'retry') {
            throw new Error(outcome.message ?? t('errStartingPayment'))
          }
          setSimulationMode(true)
          setSessionReady(true)
          return
        }

        // Cotización de envío/entrega vencida / borrada / de otro local: el
        // servidor rechaza ANTES de crear la sesión de pago (nadie pagó nada).
        // Se sale por un camino propio porque el reintento genérico repetiría
        // este MISMO body con el mismo quote muerto — un bucle. La única salida
        // es re-cotizar la dirección.
        if (res.status === 409) {
          const data = (await res.json().catch(() => ({}))) as { error?: string; reason?: string; message?: string }
          if (data.error === 'delivery_unavailable') {
            setDeliveryExpired(true)
            setError(t('deliveryQuoteExpired'))
            setSessionReady(true)
            return
          }
          // `shipping_unavailable` con reason "consumed"/"quote_in_use": alguien
          // más (otra pestaña, o el sello del pedido) ya se adelantó con ESTE
          // quote. Mismo criterio que delivery: no hay reintento del mismo body
          // que sirva, la salida es re-cotizar.
          if (data.error === 'shipping_unavailable') {
            setShipExpired(true)
            setError(t(shipErrorMessageKey(data.reason ?? 'quote_failed')))
            setSessionReady(true)
            return
          }
          // Plato o ingrediente agotado: se resuelve en el carrito, no reintentando
          // el mismo body. El padre re-mira el menú fresco y quita lo que ya no se vende.
          // Súper online (R2): `out_of_stock` también, con el mensaje del servidor
          // (el menú fresco no sabe de stock). Un restaurante no trae mensaje retail.
          if (isSessionAvailabilityRejection(res.status, data) && onItemsUnavailable) {
            if (!cancelled) onItemsUnavailable(retailRejectionMessage(res.status, data) ?? undefined)
            return
          }
          // Sin ese camino, el `message` en prosa: sin preferirlo, al cliente le
          // aparecía el código crudo —"items_unavailable"— como si fuera el problema.
          throw new Error(data.message ?? data.error ?? `Session error (${res.status})`)
        }

        // Cupón rechazado (422, spec §7.2/P1-18). Llega ANTES de que exista
        // sesión de pago: NADIE PAGÓ NADA. Sale por su propio camino porque el
        // error genérico de abajo diría "no pudimos iniciar el pago" —
        // incomprensible para alguien cuyo único problema es que el cupón se
        // agotó— y el botón de reintentar repetiría el MISMO body con el mismo
        // cupón muerto, en bucle. La salida es volver al carrito sin el cupón.
        if (res.status === 422) {
          const body = await res.json().catch(() => null)
          const rejection = parseSessionCouponRejection(res.status, body)
          if (rejection && onCouponRejected) {
            if (!cancelled) onCouponRejected(rejection)
            return
          }
          const data = (body ?? {}) as { error?: string; reason?: string; message?: string }
          // `shipping_unavailable` con el resto de reasons (expired/fingerprint_mismatch/
          // not_shippable_items/state_blocked/stock_short) y `tax_mismatch`
          // (G4: el destino ya no está en el estado del local) — mismo camino de
          // "re-cotizar", nunca reintentar el body muerto.
          if (data.error === 'shipping_unavailable' || data.error === 'tax_mismatch') {
            setShipExpired(true)
            setError(t(shipErrorMessageKey(data.error === 'tax_mismatch' ? 'quote_failed' : (data.reason ?? 'quote_failed'))))
            setSessionReady(true)
            return
          }
          // Plato fuera de su horario (422 item_unavailable): mismo camino que el
          // agotado del 409, volver al carrito sin él en vez de reintentar. Súper
          // online (R2): las líneas retail que no se pueden vender así (peso,
          // variante, no se vende online…) vuelven con el mensaje del servidor.
          if (isSessionAvailabilityRejection(res.status, data) && onItemsUnavailable) {
            if (!cancelled) onItemsUnavailable(retailRejectionMessage(res.status, data) ?? undefined)
            return
          }
          throw new Error(data.message ?? data.error ?? `Session error (${res.status})`)
        }

        // Ronda de fix 1, IMPORTANT-2: `resolveTrustedShippingQuote` puede
        // fallar con un error de INFRAESTRUCTURA (no de negocio) y /session
        // responde 502 `{error:"shipping_unavailable", reason:"quote_failed"}`
        // — SIN esta rama caía al `!res.ok` genérico de abajo, que muestra el
        // código crudo con un botón de "reintentar" que repite el MISMO body
        // (el quote sigue igual de roto: el reintento nunca puede funcionar).
        // Mismo camino de re-cotizar que 409/422.
        if (res.status === 502) {
          const data = (await res.json().catch(() => ({}))) as { error?: string; reason?: string }
          if (data.error === 'shipping_unavailable') {
            setShipExpired(true)
            setError(t(shipErrorMessageKey(data.reason ?? 'quote_failed')))
            setSessionReady(true)
            return
          }
          throw new Error(data.error ?? `Session error (${res.status})`)
        }

        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string }
          throw new Error(data.error ?? `Session error (${res.status})`)
        }

        const data = (await res.json()) as {
          token?: string
          demo?: boolean
          intentId?: string
          noChargeRequired?: boolean
          northTransactionId?: string
          coupon?: CouponSnapshot | null
        }
        if (cancelled) return
        intentIdRef.current = data.intentId ?? null

        if (data.demo) {
          setDemoMode(true)
          setSimulationMode(true)
          setSessionReady(true)
          return
        }

        // Camino $0 (spec §5.7): el cupón cubre el pedido entero. NO hay sesión
        // de pago porque no hay nada que cobrar — North jamás ve un $0. La orden
        // se crea directo con el `northTransactionId` que EMITE EL SERVIDOR
        // (`zero_<intentId>`, derivado del intent): se usa VERBATIM para que dos
        // envíos del mismo pedido choquen contra el UNIQUE de la tabla en vez de
        // regalar una segunda orden y quemar otro canje del cupón.
        //
        // Sin esta rama, un cupón del 100% (los hay vivos en producción) moría
        // acá abajo en `if (!data.token)` con "no pudimos iniciar el pago".
        if (data.noChargeRequired) {
          if (!data.northTransactionId) throw new Error(t('errStartingPayment'))
          setVerifying(true)
          let zeroOrder: SubmittedOrder | null = null
          let zeroErr: unknown = null
          let zeroRewardRejection: VisitRewardRejection | null = null
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              zeroOrder = await submitOrder(data.northTransactionId, null, data.coupon ?? null)
              break
            } catch (err) {
              zeroErr = err
              // Premio de visitas ya usado entre /session y la escritura (409
              // not_available): no se reintenta; se suelta el premio con «en uso».
              // Solo con `visitRewardId`: sin premio, este bloque no corre.
              if (visitRewardId && onVisitRewardRejected) {
                const e = err as { status?: unknown; body?: unknown }
                zeroRewardRejection = typeof e?.status === 'number' ? parseZeroWriteVisitRewardRejection(e.status, e.body) : null
                if (zeroRewardRejection) break
              }
              if (attempt < 2) await new Promise((r) => setTimeout(r, 800 * (attempt + 1)))
            }
          }
          if (cancelled || !mountedRef.current) return
          if (zeroRewardRejection && onVisitRewardRejected) {
            setVerifying(false)
            onVisitRewardRejected(zeroRewardRejection)
            return
          }
          if (!zeroOrder) throw zeroErr instanceof Error ? zeroErr : new Error(t('errProcessingOrder'))
          successFiredRef.current = true
          onSuccess(zeroOrder.id, orderPaymentReference(zeroOrder.id, zeroOrder.order_number), null, zeroOrder.walletSaveUrl, zeroOrder.accountClaimUrl ?? null, zeroOrder.trackingUrl, zeroOrder.welcomeCoupon, { substitutionPref: zeroOrder.substitutionPref, substitutionWarnings: zeroOrder.substitutionWarnings, conversion: zeroOrder.conversion })
          return
        }

        if (!data.token) throw new Error(t('errStartingPayment'))
        token = data.token
        sessionTokenRef.current = token
        if (onHostedHandoff) {
          // Dominio propio: el pedido ya quedó guardado en el intent de /session;
          // el cobro sigue en useredy.com. sessionReady queda en false a propósito:
          // ni el form ni el sondeo de rescate arrancan en esta página.
          setHandingOff(true)
          onHostedHandoff(token)
          return
        }
        setSessionReady(true)
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : t('errStartingPayment'))
        setSessionReady(true)
        return
      }

      try {
        await loadCheckoutScript()
        await waitForCheckoutGlobal()
        if (cancelled) return
        // El div #north-checkout-form lo renderiza React al pasar sessionReady=true
        // (va detrás de `showForm`). Con el script precalentado, load+wait resuelven
        // en microtasks y el mount llegaría ANTES de que React confirme ese render →
        // North lanza "Element not found" y cae en errInitForm. Esperamos al commit
        // del contenedor antes de montar.
        await waitForElement('north-checkout-form')
        if (cancelled) return
        await window.checkout!.mount(token, 'north-checkout-form')
        if (cancelled || !mountedRef.current) return
        setFormMounted(true)

        // North dispara esto cuando corre su propio submit.
        if (window.checkout!.onPaymentComplete) {
          unsubscribe = window.checkout!.onPaymentComplete((data) => {
            if (!mountedRef.current) return
            void handlePaymentComplete(data)
          })
        }
      } catch (err) {
        if (cancelled) return
        Sentry.captureException(err, {
          tags: { zone: 'microsite-payment', step: 'checkout-mount' },
        })
        setError(t('errInitForm'))
      }
    }

    init()

    return () => {
      cancelled = true
      unsubscribe?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCounter])

  // northTransactionId es concreto (real o `fallback_…`/`sim_…` ya generado por
  // el caller) para que reintentar este POST sea IDEMPOTENTE: /api/online-orders
  // deduplica por north_transaction_id, así un reintento tras un fallo transitorio
  // devuelve la misma orden en vez de crear una duplicada.
/** Lo que devuelve submitOrder: la orden más los links de Wallet y de cuenta que el servidor firmó. */
type SubmittedOrder = {
  id: string
  order_number: string | null
  walletSaveUrl: string | null
  accountClaimUrl: string | null
  trackingUrl: string | null
  welcomeCoupon: WelcomeCoupon | null
  substitutionPref?: SubstitutionPref
  substitutionWarnings?: SubstitutionWarning[]
  conversion?: Conversion
}

  async function submitOrder(
    northTransactionId: string,
    northAuthGuid: string | null,
    // Camino $0 ÚNICAMENTE: el snapshot que devolvió /session. La escritura lo
    // re-valida contra la fila VIVA antes de regalar la orden (route.ts), así
    // que reenviarlo no es confiar en el cliente — es ahorrarle una lectura. En
    // el camino con cobro el cupón viaja por el intent y esto va null.
    zeroChargeCoupon: CouponSnapshot | null = null,
  ) {
    const res = await fetch('/api/online-orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // Modo mesa (QR): NO se manda location_id — la tenencia sale del qr_token
        // firmado (server-side). El chequeo de conflicto del server queda como
        // defensa contra atacantes, no contra nuestro propio cliente.
        ...(tableContext
          ? { qr_token: tableContext.qrToken, qr_order_type: qrOrderType ?? "dine_in" }
          : { location_id: locationId }),
        items: buildMicrositeOrderItems(cart),
        customer_name: customer.name.trim(),
        // `|| undefined` omite la clave sin teléfono (ver nota en el POST de
        // sesión): un dine_in de mesa manda customer_phone="" y el schema lo
        // rechazaba como E.164 inválido pese al qr_token. Omitido, el refine
        // `qr_token || customer_phone` lo acepta.
        customer_phone: normalizePhone(customer.phone) || undefined,
        customer_email: customer.email.trim() || undefined,
        // Nota libre para la cocina → columna `orders.notes`. Antes iba como
        // `pickup_time` (timestamptz) y reventaba el insert ("cobrado, sin orden").
        notes: customer.pickupNote.trim() || undefined,
        // Task 11: SOLO telemetría (reorder_complete) — el servidor jamás la usa
        // para precio ni tenencia. Se omite en vez de mandar `false` para que un
        // grep de "from_prefill" en logs/DB signifique siempre "sí, fue reorder".
        ...(fromPrefill ? { from_prefill: true } : {}),
        // Medición de anuncios (solo telemetría): mismo campo que en /session,
        // para el camino $0 y la simulación, donde este POST sí crea la orden.
        ...(tableContext ? {} : attributionBodyField()),
        // Ver la nota del POST de sesión: derivado del teléfono, pero el
        // servidor lo re-deriva y solo él consulta la revocación real (STOP).
        sms_opt_in: !!normalizePhone(customer.phone),
        // "No quiero SMS" (30923): la ÚNICA señal de consent del cliente que el
        // servidor honra, y solo hacia ABAJO (registra revocación; jamás sube).
        sms_decline: customer.smsDecline === true,
        // Consentimiento de MARKETING (promociones del restaurante) — señal
        // separada de sms_decline: esta SÍ puede subir el consentimiento
        // (ver lib/marketing/consent.ts). Ausente/false nunca revoca nada.
        marketing_opt_in: customer.marketingOptIn === true,
        // Sustituciones (spec sustituciones §4.2): mismo criterio que el body
        // de /session — se omite con el flag apagado. Las líneas individuales
        // (substitution_pref/substitution_item_id por ítem del carrito) las
        // manda buildMicrositeOrderItems, no este bloque.
        ...(substitutionsEnabled ? { substitution_pref: substitutionPref } : {}),
        donation_amount: donAmount,
        donation_charge: donCharge,
        donation_campaign_id: donationCampaignId ?? null,
        north_transaction_id: northTransactionId,
        ...(northAuthGuid ? { north_auth_guid: northAuthGuid } : {}),
        // Ancla al carrito validado pre-cobro. Se omite si /session no creó
        // intent: el servidor trata su ausencia como "sin ancla", no como error.
        ...(intentIdRef.current ? { intent_id: intentIdRef.current } : {}),
        ...(zeroChargeCoupon ? { coupon: zeroChargeCoupon } : {}),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(scheduledFor ? { scheduled_for: scheduledFor } : {}),
        // Delivery (Fase 4b). `delivery_fee_cents` es EXACTAMENTE el valor congelado
        // de la cotización: el servidor re-precia el pedido con ESE fee y compara
        // contra lo que North autorizó. Un centavo distinto = 422 con el cobro ya
        // capturado. Nunca recalcular, nunca redondear, nunca derivar de `deliveryFee`
        // (ése es el display en dólares y puede tener polvo de punto flotante).
        ...(isDelivery
          ? {
              order_type: "delivery",
              delivery_type: "delivery",
              // Con apto/suite — es lo que lee el repartidor. Recortado a 300 porque
              // así lo valida el schema, y un 400 acá sería "cobrado, sin orden".
              delivery_address: formatDropoffFull(dropoff!).slice(0, 300),
              delivery_fee_cents: deliveryQuote!.feeAmountCents,
              // Fase 5: con el quoteId el servidor despacha el courier solo tras
              // crear la orden. Es la llave a la dirección ESTRUCTURADA (lat/lng)
              // que quedó congelada en delivery_quotes — el string de arriba es
              // para leer, no para geocodificar. Si falta, la orden se crea igual
              // y el dueño despacha a mano.
              delivery_quote_id: deliveryQuoteId!,
              ...(tipCents > 0 ? { delivery_tip_cents: tipCents } : {}),
            }
          : {}),
      }),
    })
    const data = (await res.json()) as {
      order?: { id: string; order_number: string | null }
      // El servidor decide plataforma y firma (lib/wallet/saveLink.ts): aquí solo
      // se transporta. Llega null en iPhone y si falta configuración.
      walletSaveUrl?: string | null
      // El servidor acuña el link de reclamo de cuenta (o null); aquí solo se transporta.
      accountClaimUrl?: string | null
      // El servidor acuña el link del seguimiento en vivo (o null); aquí solo se transporta.
      trackingUrl?: string | null
      substitutionPref?: SubstitutionPref
      substitutionWarnings?: SubstitutionWarning[]
      conversion?: unknown
      error?: string
    }
    if (!res.ok || !data.order) {
      // Mismo mensaje de siempre; `status`/`body` solo los lee el camino $0 con
      // premio (parseZeroWriteVisitRewardRejection), nadie más.
      throw Object.assign(new Error(data.error ?? t('errProcessingOrder')), { status: res.status, body: data })
    }
    return {
      ...data.order,
      walletSaveUrl: data.walletSaveUrl ?? null,
      accountClaimUrl: data.accountClaimUrl ?? null,
      trackingUrl: data.trackingUrl ?? null,
      welcomeCoupon: parseWelcomeCoupon(data),
      substitutionPref: data.substitutionPref,
      substitutionWarnings: data.substitutionWarnings,
      conversion: parseConversion(data.conversion),
    }
  }

  // Called by onPaymentComplete — verify server-side before creating order
  async function handlePaymentComplete(_data: unknown) {
    setVerifying(true)
    setError(null)
    try {
      const token = sessionTokenRef.current
      if (!token) throw new Error(t('errSessionExpired'))

      const completeRes = await fetch('/api/online-orders/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, locationId }),
      })
      const completeData = (await completeRes.json()) as { transactionId?: string; authGuid?: string; cancelToken?: string; error?: string }
      if (!completeRes.ok || completeData.error) {
        throw new Error(completeData.error ?? t('errPaymentDeclined'))
      }
      // transactionId may be null if North omits tran_nbr — payment was still
      // approved server-side. Generate the fallback id ONCE here (not per attempt)
      // so retries stay idempotent against /api/online-orders.
      const txnId =
        completeData.transactionId ?? `fallback_${Date.now()}_${Math.random().toString(36).slice(2)}`
      const authGuid = completeData.authGuid ?? null
      // Token de un solo uso para cancel-charge: /complete solo lo manda si el
      // cobro se quedó sin orden, y sin él el servidor no revierte (S4-5).
      const cancelToken = completeData.cancelToken ?? null

      // El cobro YA ocurrió. Crear la orden con reintentos idempotentes para no
      // perderla por un fallo transitorio (y no re-cobrar — reusamos el mismo
      // txnId). Si tras los reintentos no se pudo crear, revertimos el cobro.
      let order: SubmittedOrder | null = null
      let lastErr: unknown = null
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          order = await submitOrder(txnId, authGuid)
          break
        } catch (err) {
          lastErr = err
          if (attempt < 2) await new Promise((r) => setTimeout(r, 800 * (attempt + 1)))
        }
      }

      if (!order) {
        // Cobrado pero sin orden: revertir el cobro para no dejar un cargo huérfano.
        // cancel-charge solo revierte con el token de esta sesión, una vez, y no
        // revierte si la orden sí existe. Sin token, el cobro lo recoge la orden de
        // rescate del barrido y el dueño lo reembolsa desde el panel.
        if (authGuid && cancelToken) {
          try {
            await fetch('/api/online-orders/cancel-charge', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ locationId, authGuid, transactionId: txnId, cancelToken }),
            })
          } catch {
            // El fallo se registra server-side; el mensaje al cliente va abajo.
          }
        }
        Sentry.captureException(lastErr ?? new Error('submitOrder failed after charge'), {
          tags: { zone: 'microsite-payment', step: 'charged-no-order' },
          extra: { txnId, hasAuthGuid: !!authGuid, hasCancelToken: !!cancelToken },
        })
        throw new Error(t('errChargedNoOrder'))
      }

      if (!mountedRef.current) return
      // S5 numeración limpia: una txn simulada/fallback (queda pending, sin
      // número hasta confirmarse) muestra la referencia UUID en vez de "#null".
      // Un cobro real siempre trae el número asignado en la respuesta.
      successFiredRef.current = true
      onSuccess(order.id, orderPaymentReference(order.id, order.order_number), sessionTokenRef.current, order.walletSaveUrl, order.accountClaimUrl ?? null, order.trackingUrl, order.welcomeCoupon, { substitutionPref: order.substitutionPref, substitutionWarnings: order.substitutionWarnings, conversion: order.conversion })
    } catch (e) {
      if (!mountedRef.current) return
      setError(e instanceof Error ? e.message : t('errUnknown'))
    } finally {
      if (mountedRef.current) setVerifying(false)
    }
  }

  // Simulation mode only — North form not available
  async function handleSimulationPay() {
    setVerifying(true)
    setError(null)
    try {
      const northTransactionId = `sim_${Date.now()}_${Math.random().toString(36).slice(2)}`
      const order = await submitOrder(northTransactionId, null)
      if (!mountedRef.current) return
      successFiredRef.current = true
      onSuccess(order.id, orderPaymentReference(order.id, order.order_number), null, order.walletSaveUrl, order.accountClaimUrl ?? null, order.trackingUrl, order.welcomeCoupon, { substitutionPref: order.substitutionPref, substitutionWarnings: order.substitutionWarnings, conversion: order.conversion })
    } catch (e) {
      if (!mountedRef.current) return
      setError(e instanceof Error ? e.message : t('errUnknown'))
    } finally {
      if (mountedRef.current) setVerifying(false)
    }
  }

  const showForm = sessionReady && !simulationMode && !error

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <button
          type="button"
          onClick={onBack}
          disabled={verifying}
          className="text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          ← {t('back')}
        </button>
        <h2 className="flex-1 text-center text-xl font-bold text-gray-900 -ml-12">
          {t('payment')}
        </h2>
      </div>

      {/* Compact order summary */}
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-4">
        <span className="text-xs text-gray-500 truncate">
          {cart.map(i => `${i.quantity}× ${i.name}`).join(', ')}
        </span>
        <div className="flex-shrink-0 text-right text-xs text-gray-500 space-x-3">
          <span>{t('subShort')} ${subtotal.toFixed(2)}</span>
          {/* El descuento se muestra acá también: el resumen de esta pantalla es
              lo último que el cliente lee antes de teclear su tarjeta, y sin la
              línea el total con cupón parecería un error de cuentas. */}
          {couponDiscount > 0 && couponCode && (
            <span style={{ color: accentColor }} className="font-semibold">
              {couponCode} −${couponDiscount.toFixed(2)}
            </span>
          )}
          {couponDiscount > 0 && visitRewardId && visitRewardLabel && (
            <span style={{ color: accentColor }} className="font-semibold">
              {visitRewardLabel} −${couponDiscount.toFixed(2)}
            </span>
          )}
          <span>{t(taxIncluded ? 'taxIncludedShort' : 'taxShort')} ${taxAmount.toFixed(2)}</span>
          {processingFee > 0 && <span>{t('feeShort')} ${processingFee.toFixed(2)}</span>}
          {(deliveryFee > 0 || (isShip && shipQuote?.freeShippingApplied)) && (
            <span>
              {isShip ? t('shippingShort') : t('deliveryShort')}{' '}
              {isShip && shipQuote?.freeShippingApplied && deliveryFee === 0
                ? t('shippingFeeFree')
                : `$${deliveryFee.toFixed(2)}`}
            </span>
          )}
          {tipCents > 0 && <span>{t('driverTipShort')} ${(tipCents / 100).toFixed(2)}</span>}
          <span className="font-bold text-gray-900 text-sm">{tCommon('total')} ${total.toFixed(2)}</span>
        </div>
      </div>

      {/* Accepted card brands — visible regardless of North form state */}
      <div className="px-5 py-3 border-b border-gray-100 flex justify-center">
        <AcceptedCards tone="light" />
      </div>

      {/* Body */}
      <div
        className="flex-1 min-h-0 flex flex-col px-5 pt-4 gap-3"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        {/* Simulation mode */}
        {simulationMode && (
          <>
            {/* Ícono dibujado, no un emoji dentro de la cadena de i18n. */}
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900 flex-shrink-0 flex items-start gap-2">
              {demoMode ? (
                <FlaskConical className="w-4 h-4 flex-shrink-0 mt-px" />
              ) : (
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-px" />
              )}
              <span className="flex-1">{demoMode ? t('demoMode') : t('simulationMode')}</span>
            </div>
            <button
              type="button"
              onClick={handleSimulationPay}
              disabled={verifying}
              className="w-full rounded-full py-4 text-white text-base font-bold transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ backgroundColor: accentColor }}
            >
              {verifying ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> {t('processing')}</>
              ) : (
                `${t('pay')} $${total.toFixed(2)}`
              )}
            </button>
          </>
        )}

        {/* Loading: creating session */}
        {!sessionReady && !error && (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {handingOff ? t('redirectingToPayment') : t('startingPayment')}
          </div>
        )}

        {/* Selector de "si algo no está disponible" (spec sustituciones §4.2).
            En el checkout online vive en CustomerStep (el paso anterior): esta
            pantalla manda la preferencia a /session AL MONTARSE y, con pago en
            dominio propio, redirige enseguida — elegirla acá no llegaba al
            pedido. Solo queda acá para el pedido de mesa por QR, que no pasa
            por CustomerStep. */}
        {substitutionsEnabled && tableContext && (
          <div className="mb-4">
            <SubstitutionPrefSelector
              value={substitutionPref}
              onChange={onSubstitutionPrefChange}
              contactAllowed={contactAllowed}
              accentColor={accentColor}
            />
          </div>
        )}

        {/* Consentimiento pasivo — reemplaza el checkbox obligatorio que vivía en
            CustomerStep. Va aquí porque es donde convergen los dos flujos (mesa
            llega del carrito, online de CustomerStep) y donde está el formulario
            de tarjeta: la nota tiene que ser conspicua y adyacente al acto de
            pagar. Dentro del botón de North no se puede — vive en un iframe. */}
        <p className="flex-shrink-0 text-[11px] leading-relaxed text-gray-500 text-center">
          {t.rich('passiveConsent', {
            // Van LOS DOS: la del restaurante (lo que North pidió) y la de
            // plataforma. Este es el punto donde el cliente acepta la relación de
            // procesamiento — quitar de aquí la divulgación de Redy sería una
            // regresión legal, no una simplificación.
            merchantRefund: (chunks) => (
              <a href={`/order/${slug}/refund-policy`} target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: accentColor }}>{chunks}</a>
            ),
            refund: (chunks) => (
              <a href="/refund" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: accentColor }}>{chunks}</a>
            ),
            terms: (chunks) => (
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: accentColor }}>{chunks}</a>
            ),
          })}
        </p>

        {/* North Form — fills all remaining space; North renders its own submit button */}
        {showForm && (
          <div className="flex-1 min-h-0 relative">
            <div id="north-checkout-form" className="absolute inset-0" />
            {/* Loading overlay while form mounts */}
            {!formMounted && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/90">
                <Loader2 className="w-4 h-4 animate-spin text-gray-500 mr-2" />
                <span className="text-sm text-gray-500">{t('loadingForm')}</span>
              </div>
            )}
            {/* Verifying overlay after onPaymentComplete fires */}
            {verifying && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
                <Loader2 className="w-5 h-5 animate-spin text-gray-700 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t('verifyingPayment')}</span>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-3 flex-shrink-0">
            <p className="text-sm text-red-600 text-center">{error}</p>
            {/* Cotización vencida: el reintento genérico NO aplica (repetiría el
                mismo quote muerto en bucle). Única salida: re-cotizar la dirección. */}
            {deliveryExpired ? (
              <button
                type="button"
                onClick={() => onDeliveryQuoteExpired?.()}
                className="rounded-full px-5 py-2 text-white text-sm font-bold transition hover:opacity-95"
                style={{ backgroundColor: accentColor }}
              >
                {t('deliveryQuoteExpiredCta')}
              </button>
            ) : shipExpired ? (
              <button
                type="button"
                onClick={() => onShipQuoteExpired?.()}
                className="rounded-full px-5 py-2 text-white text-sm font-bold transition hover:opacity-95"
                style={{ backgroundColor: accentColor }}
              >
                {t('shipQuoteExpiredCta')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setRetryCounter((c) => c + 1)}
                disabled={verifying}
                className="rounded-full px-5 py-2 text-white text-sm font-bold transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: accentColor }}
              >
                {t('retry')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
